import { useLazyQuery } from '@graphcommerce/graphql'
import { AddProductsToCartForm } from '@graphcommerce/magento-product'
import { responsiveVal } from '@graphcommerce/next-ui'
import { Box, debounce, useEventCallback } from '@mui/material'
import { useEffect } from 'react'
import { useHits } from 'react-instantsearch-hooks-web'
import { UseCustomerProductPricesDocument } from '../../Product/UseCustomerProductPrices.gql'
import { useCustomerProductPrices } from '../../Product/useCustomerProductPrices'
import { ProductHit, AlgoliaProductHitItem } from './Hits/ProductHit'

type ResultsListProps = {
  size?: string
}

export function ResultsList(props: ResultsListProps) {
  const { hits } = useHits()
  const { size = 'small' } = props

  const [exec, result] = useLazyQuery(UseCustomerProductPricesDocument, {})
  const productsWithCustomerSpecificPrices = useCustomerProductPrices(result.data?.products)

  const debouncedExec = useEventCallback(debounce(exec, 200))
  useEffect(() => {
    const skus = hits.map((i) => {
      if (Array.isArray(i.sku)) return i.sku[0] as string
      return i.sku as string
    })

    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    debouncedExec({ variables: { pageSize: 100, skus } })

    // restore scroll position on pageload
    const resultNodes = document.querySelectorAll('.MuiButtonBase-root.ProductListItem-root')
    if (resultNodes.length > 0 && localStorage.getItem('clickedAlgoliaResult')) {
      const previouslyClickedResultHref = localStorage.getItem('clickedAlgoliaResult')
      const scrollToNode = Array.from(resultNodes).find(
        (node) => node.getAttribute('href') === previouslyClickedResultHref,
      )
      scrollToNode?.scrollIntoView({ behavior: 'smooth' })
      localStorage.removeItem('clickedAlgoliaResult')
    }
  }, [debouncedExec, hits])

  return (
    <AddProductsToCartForm>
      <Box
        sx={[
          (theme) => ({
            marginTop: theme.spacings.md,
            display: 'grid',
            gridColumnGap: theme.spacings.md,
            gridRowGap: theme.spacings.md,
          }),
          size === 'normal' && {
            gridTemplateColumns: {
              xs: `repeat(2, 1fr)`,
              sm: `repeat(3, 1fr)`,
              md: `repeat(4, 1fr)`,
              lg: `repeat(5, 1fr)`,
              xl: `repeat(6, 1fr)`,
            },
          },
          size === 'small' && {
            gridTemplateColumns: `repeat(auto-fill, minmax(${responsiveVal(150, 280)}, 1fr))`,
          },
          size === 'normal' && {
            gridTemplateColumns: {
              xs: `repeat(2, 1fr)`,
              md: `repeat(3, 1fr)`,
              lg: `repeat(4, 1fr)`,
            },
          },
        ]}
        {...props}
      >
        {hits.map((hit) => (
          <ProductHit
            key={hit.objectID}
            hit={hit as unknown as AlgoliaProductHitItem}
            price={
              productsWithCustomerSpecificPrices?.items?.find((i) => i?.sku === (hit.sku as string))
                ?.price_range.minimum_price
            }
          />
        ))}
      </Box>
    </AddProductsToCartForm>
  )
}
